/**
 * Business-hours arithmetic in a named IANA timezone.
 *
 * Every SLA number in the product is measured in *business* minutes, not wall
 * minutes: mail that arrives at 17:59 on Friday and is answered at 09:01 on
 * Monday kept the customer waiting one minute, not sixty-three hours. Reporting
 * that as a breach would be wrong, and agents would stop trusting the number.
 *
 * Two things make this harder than adding hours to a timestamp:
 *
 *   1. Europe/London is not a fixed offset. It is UTC+0 in winter and UTC+1 in
 *      summer, so "09:00 local" is a different instant depending on the date,
 *      and the clocks change *inside* a working day twice a year. Everything
 *      below therefore converts local wall time to an instant per calendar day
 *      rather than assuming a constant offset.
 *
 *   2. A closed day is not just Saturday and Sunday -- bank holidays are
 *      configured by an admin and change yearly, so the closed set is data, not
 *      a hard-coded rule.
 *
 * No dependencies on purpose: this module runs in the API *and* in the browser
 * bundle, so the countdown an agent watches is computed by the same code that
 * produced the number the server sorted the queue by.
 */
/** 09:00-18:00. */
const MON_TO_THU = { start: 9 * 60, end: 18 * 60 };
/** 09:00-17:00 -- Friday closes an hour early. */
const FRIDAY = { start: 9 * 60, end: 17 * 60 };
/**
 * NetXL's support hours. Bank holidays are layered on at runtime from the
 * `bank_holidays` table, so this constant stays a pure weekly pattern.
 */
export const NETXL_BUSINESS_WEEK = [
    null, // Sunday
    MON_TO_THU,
    MON_TO_THU,
    MON_TO_THU,
    MON_TO_THU,
    FRIDAY,
    null, // Saturday
];
export const NETXL_CALENDAR = {
    timeZone: "Europe/London",
    week: NETXL_BUSINESS_WEEK,
    holidays: [],
};
/** Build a calendar from the configured holiday list. */
export function calendarWithHolidays(holidays, base = NETXL_CALENDAR) {
    return { ...base, holidays };
}
// ─────────────────────────────────────────────────────────────────────────────
// Timezone primitives.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Formatters are expensive to construct and this runs per day of every span,
 * so they are built once per zone. The cache is unbounded only in theory --
 * the process uses exactly one zone.
 */
const formatters = new Map();
function formatterFor(timeZone) {
    let formatter = formatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-GB", {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            // h23 rather than hour12:false: some engines render midnight as "24"
            // under hour12:false, which would shift the day by one.
            hourCycle: "h23",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        formatters.set(timeZone, formatter);
    }
    return formatter;
}
function partsIn(instant, timeZone) {
    const parts = formatterFor(timeZone).formatToParts(instant);
    const read = (type) => {
        const found = parts.find((part) => part.type === type);
        return found ? Number(found.value) : 0;
    };
    return {
        year: read("year"),
        month: read("month"),
        day: read("day"),
        hour: read("hour"),
        minute: read("minute"),
        second: read("second"),
    };
}
/** The zone's UTC offset at a given instant, in milliseconds. */
function offsetMsAt(instant, timeZone) {
    const local = partsIn(instant, timeZone);
    const asIfUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    // Discard sub-second precision so the difference is a clean offset.
    return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
}
/**
 * The instant at which a given local wall time occurs.
 *
 * Resolved in two passes because the offset must be sampled *at the answer*,
 * not at the guess: on a spring-forward morning the naive guess lands in the
 * previous offset and a single pass would be an hour out. The second pass
 * re-samples using the corrected instant, which converges for every real-world
 * transition (all of which are <= 1 hour).
 */
function localTimeToInstant(year, month, day, minutesFromMidnight, timeZone) {
    const naive = Date.UTC(year, month - 1, day, Math.floor(minutesFromMidnight / 60), minutesFromMidnight % 60);
    let instant = naive - offsetMsAt(new Date(naive), timeZone);
    instant = naive - offsetMsAt(new Date(instant), timeZone);
    return new Date(instant);
}
/** "YYYY-MM-DD" for a local calendar date. */
function dateKey(year, month, day) {
    const mm = String(month).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
}
/** Weekday index (0 = Sunday) for a local calendar date. */
function weekdayOf(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}
function nextDay({ year, month, day }) {
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    return {
        year: next.getUTCFullYear(),
        month: next.getUTCMonth() + 1,
        day: next.getUTCDate(),
    };
}
/**
 * The open window for a local date as a pair of instants, or null if closed.
 * Bank holidays and closed weekdays both resolve to null here, so callers never
 * need to know which reason applied.
 */
function windowFor(date, calendar, holidays) {
    if (holidays.has(dateKey(date.year, date.month, date.day)))
        return null;
    const hours = calendar.week[weekdayOf(date.year, date.month, date.day)];
    if (!hours)
        return null;
    return {
        open: localTimeToInstant(date.year, date.month, date.day, hours.start, calendar.timeZone),
        close: localTimeToInstant(date.year, date.month, date.day, hours.end, calendar.timeZone),
    };
}
/**
 * Guard against a caller passing a far-future or malformed bound and spinning
 * this into a hang. Ten years of days is far beyond any real SLA span.
 */
const MAX_DAYS_SCANNED = 3660;
// ─────────────────────────────────────────────────────────────────────────────
// Public API.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Midnight at the start of an instant's local day, in the calendar's zone.
 *
 * Exported because reporting needs "today", "this week" and "this month" to
 * mean the same thing the SLA clock means -- London days, not UTC ones. During
 * BST those differ by an hour, which is a whole hour of mail landing in the
 * wrong day's figures.
 */
export function startOfDay(instant, calendar) {
    const local = partsIn(instant, calendar.timeZone);
    return localTimeToInstant(local.year, local.month, local.day, 0, calendar.timeZone);
}
/** Midnight at the start of the Monday on or before an instant's local day. */
export function startOfWeek(instant, calendar) {
    const local = partsIn(instant, calendar.timeZone);
    const weekday = weekdayOf(local.year, local.month, local.day);
    // Monday-based: Sunday (0) belongs to the week that started six days earlier.
    const back = (weekday + 6) % 7;
    const shifted = new Date(Date.UTC(local.year, local.month - 1, local.day - back));
    return localTimeToInstant(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate(), 0, calendar.timeZone);
}
/** Midnight on the first of an instant's local month. */
export function startOfMonth(instant, calendar) {
    const local = partsIn(instant, calendar.timeZone);
    return localTimeToInstant(local.year, local.month, 1, 0, calendar.timeZone);
}
/**
 * The calendar day an instant falls in, as "YYYY-MM-DD" in its own zone.
 *
 * The same shape the holiday list uses, and for the same reason: a day here is
 * a London day, so "did this happen today" survives BST -- where a 24-hour
 * window from midnight UTC straddles two of them.
 *
 * Exported because "one lunch per day" needs to agree with everything else
 * about where a day begins, and a second implementation in the API would be a
 * second answer waiting to diverge on the last Sunday in March.
 */
export function localDateKey(instant, calendar) {
    const local = partsIn(instant, calendar.timeZone);
    return dateKey(local.year, local.month, local.day);
}
/**
 * When the business closes on an instant's own day, or null if it never opens
 * that day -- a weekend, or a bank holiday.
 *
 * "Off for the rest of today" needs an end, and close of business is the only
 * honest one: midnight would pause a clock through hours that were never being
 * counted, and an arbitrary "+8 hours" would spill into tomorrow morning for
 * anybody who clicked it after lunch.
 */
export function endOfBusinessDay(instant, calendar) {
    const local = partsIn(instant, calendar.timeZone);
    const window = windowFor(local, calendar, new Set(calendar.holidays));
    return window ? window.close : null;
}
/**
 * A wall-clock time on the calendar day an instant falls in.
 *
 * "Four o'clock on the day this is due" -- the shape every deadline that is
 * stated as a date rather than a moment needs, and the reason it lives here
 * rather than beside its one caller. Europe/London is not a fixed offset, so
 * doing this with arithmetic on a timestamp is right for seven months of the
 * year; `localTimeToInstant` behind it resolves the offset in two passes so it
 * is also right on the two mornings that it is not.
 *
 * The *day* is the day in `timeZone`, not in UTC and not on whatever machine
 * is asking. A due date written at five in the afternoon in California is the
 * next morning in London, and a rule about four o'clock has to agree with the
 * office it belongs to about which day that is.
 *
 * Takes a zone rather than a `BusinessCalendar` on purpose: opening hours and
 * bank holidays have no part in this. A deadline of four o'clock is four
 * o'clock on a Sunday too.
 */
export function localTimeOnDay(instant, minutesFromMidnight, timeZone) {
    const local = partsIn(instant, timeZone);
    return localTimeToInstant(local.year, local.month, local.day, minutesFromMidnight, timeZone);
}
/** Whether the business is open at a given instant. */
export function isOpenAt(instant, calendar) {
    const holidays = new Set(calendar.holidays);
    const local = partsIn(instant, calendar.timeZone);
    const window = windowFor(local, calendar, holidays);
    if (!window)
        return false;
    return instant >= window.open && instant < window.close;
}
/**
 * Business minutes elapsed between two instants.
 *
 * Returns 0 rather than a negative number when `end` precedes `start`; a
 * reversed span means a clock that has not started, and callers treat that the
 * same as no time elapsed.
 */
export function businessMinutesBetween(start, end, calendar) {
    if (!(end > start))
        return 0;
    const holidays = new Set(calendar.holidays);
    let date = partsIn(start, calendar.timeZone);
    let total = 0;
    for (let scanned = 0; scanned < MAX_DAYS_SCANNED; scanned += 1) {
        const window = windowFor(date, calendar, holidays);
        if (window) {
            // Once the window opens after `end` there is nothing further to add and
            // every later day is later still.
            if (window.open >= end)
                break;
            const from = start > window.open ? start : window.open;
            const to = end < window.close ? end : window.close;
            if (to > from)
                total += (to.getTime() - from.getTime()) / 60_000;
        }
        // Cheap termination: a closed day cannot tell us we are past the end, so
        // compare calendar position rather than the (absent) window.
        const dayEnd = localTimeToInstant(date.year, date.month, date.day, 24 * 60, calendar.timeZone);
        if (dayEnd >= end)
            break;
        date = nextDay(date);
    }
    return total;
}
/**
 * The instant `minutes` of business time after `start`.
 *
 * Used to turn "30 minutes to respond" into a concrete deadline the UI can
 * count down to. A deadline that falls outside opening hours is pushed to the
 * next open minute rather than landing in a closed period.
 */
export function addBusinessMinutes(start, minutes, calendar) {
    const holidays = new Set(calendar.holidays);
    let date = partsIn(start, calendar.timeZone);
    let remaining = Math.max(0, minutes);
    for (let scanned = 0; scanned < MAX_DAYS_SCANNED; scanned += 1) {
        const window = windowFor(date, calendar, holidays);
        if (window) {
            const from = start > window.open ? start : window.open;
            if (from < window.close) {
                const available = (window.close.getTime() - from.getTime()) / 60_000;
                if (available >= remaining) {
                    return new Date(from.getTime() + remaining * 60_000);
                }
                remaining -= available;
            }
        }
        date = nextDay(date);
    }
    // Unreachable for any sane target; returning the scan horizon keeps the
    // return type honest rather than throwing inside a rendering path.
    return new Date(start.getTime() + MAX_DAYS_SCANNED * 86_400_000);
}
//# sourceMappingURL=calendar.js.map